import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from "react"
import { Trans, useLingui } from "@lingui/react/macro"
import { createPortal } from "react-dom"
import type {
  DealDrawer as DealDrawerData,
  LinkedDealPipelineStage,
} from "@/types/v2/conversation"
import {
  buildStageJourneyTints,
  STAGE_DOT_HEX,
  STAGE_SELECTED_BG,
  STAGE_SELECTED_FG,
} from "@/lib/deal-stage"
import {
  useStageMove,
  type DealPropKey,
} from "@/hooks/use-stage-move"

// `StagePicker` — clickable Stage chip on the Properties
// card that opens a "Move to stage" popover.
//
// Matches Paper MQV-0: trigger is a neutral chip with a
// stage_type-coded dot + chevron (no journey tint — the
// Properties row doesn't carry pipeline-position context);
// popover renders one option per pipeline stage, ordered
// by position. Each unselected row wears the journey tint
// for its position; the selected row overlays the dark
// fill on top of that tint. Esc closes; clicking outside
// closes.
//
// The popover is rendered into a `document.body` portal
// so it escapes the Properties card's `overflow-clip`
// container — without the portal, the last few stage
// options get visually cut off even though they're in
// the DOM.
//
// Submission goes through the existing v2 endpoint
// `PATCH /v2/deals/stages/:to_stage_id/cards/:deal_id`
// — the same one the kanban drag-and-drop uses. The
// optimistic update lives in `router.replaceProp("deal")`
// so the chip flips immediately; ActionCable
// (`deal.updated` on `deal:<id>:details`) reconciles via
// the host page's existing subscription after the server
// confirms. On failure we revert the prop.

interface StagePickerProps {
  deal: DealDrawerData
  // Which prop on the host page holds the deal payload.
  // `deal` on the show page; `dealDrawer` on the deals
  // index where the drawer is hosted.
  dealPropKey: DealPropKey
}

const POPOVER_WIDTH = 224

export function StagePicker({ deal, dealPropKey }: StagePickerProps) {
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const menuRef = useRef<HTMLDivElement | null>(null)
  const [open, setOpen] = useState(false)
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(
    null,
  )

  // Click-outside dismissal. Both the portal'd menu and
  // the trigger live outside each other in the DOM tree,
  // so we check both before closing.
  useEffect(() => {
    if (!open) return
    const handle = (event: PointerEvent) => {
      const target = event.target as Node
      if (
        triggerRef.current?.contains(target) ||
        menuRef.current?.contains(target)
      ) {
        return
      }
      setOpen(false)
    }
    document.addEventListener("pointerdown", handle)
    return () => document.removeEventListener("pointerdown", handle)
  }, [open])

  // Re-measure on open and on viewport resize/scroll while
  // open. The deal drawer scrolls internally, so we listen
  // on the closest scroll container too via a capture-phase
  // `scroll` listener.
  useEffect(() => {
    if (!open) return

    const reposition = () => {
      if (triggerRef.current) {
        setAnchorRect(triggerRef.current.getBoundingClientRect())
      }
    }
    reposition()

    window.addEventListener("resize", reposition)
    document.addEventListener("scroll", reposition, true)
    return () => {
      window.removeEventListener("resize", reposition)
      document.removeEventListener("scroll", reposition, true)
    }
  }, [open])

  const toggleOpen = () => {
    if (!open && triggerRef.current) {
      setAnchorRect(triggerRef.current.getBoundingClientRect())
    }
    setOpen((prev) => !prev)
  }

  return (
    <>
      <Trigger
        ref={triggerRef}
        deal={deal}
        open={open}
        onClick={toggleOpen}
      />
      {open && anchorRect && typeof document !== "undefined"
        ? createPortal(
            <StageMenu
              ref={menuRef}
              deal={deal}
              dealPropKey={dealPropKey}
              anchorRect={anchorRect}
              onClose={() => setOpen(false)}
            />,
            document.body,
          )
        : null}
    </>
  )
}

interface TriggerProps {
  deal: DealDrawerData
  open: boolean
  onClick: () => void
  ref: React.Ref<HTMLButtonElement>
}

function Trigger({ deal, open, onClick, ref }: TriggerProps) {
  const { t } = useLingui()
  return (
    <button
      ref={ref}
      type="button"
      onClick={onClick}
      aria-haspopup="listbox"
      aria-expanded={open}
      aria-label={t`Stage: ${deal.stage.name}. Click to change.`}
      data-testid="stage-picker-trigger"
      className="flex items-center rounded-full py-1 px-2.5 gap-1.5 bg-card border border-[#C9C2B6] cursor-pointer hover:bg-[#F7F5F0]"
    >
      <span
        aria-hidden="true"
        className="rounded-full shrink-0 size-1.5"
        style={{ backgroundColor: STAGE_DOT_HEX[deal.stage.stageType] }}
      />
      <span className="font-medium text-foreground text-[13px] leading-4">
        {deal.stage.name}
      </span>
      <ChevronDown />
    </button>
  )
}

interface StageMenuProps {
  deal: DealDrawerData
  dealPropKey: DealPropKey
  anchorRect: DOMRect
  onClose: () => void
  ref: React.Ref<HTMLDivElement>
}

function StageMenu({
  deal,
  dealPropKey,
  anchorRect,
  onClose,
  ref,
}: StageMenuProps) {
  const { t } = useLingui()
  const stages = useMemo(
    () =>
      [...deal.pipeline.stages].sort(
        (a, b) => a.position - b.position,
      ),
    [deal.pipeline.stages],
  )
  // Journey-tint background per stage row — same
  // interpolation as the kanban headers so the popover
  // reads as the same component, just stacked.
  const tints = useMemo(
    () => buildStageJourneyTints(stages),
    [stages],
  )

  const { submitting, moveToStage } = useStageMove({
    deal,
    dealPropKey,
    onSuccess: onClose,
  })

  const innerRef = useRef<HTMLDivElement | null>(null)

  // Focus the menu on mount so keyboard navigation
  // (Esc, numeric shortcuts) works without the user
  // having to click into it first.
  useEffect(() => {
    innerRef.current?.focus()
  }, [])

  const setRefs = useCallback(
    (node: HTMLDivElement | null) => {
      innerRef.current = node
      if (typeof ref === "function") ref(node)
      else if (ref) (ref as { current: HTMLDivElement | null }).current = node
    },
    [ref],
  )

  const handleKeyDown = (
    event: KeyboardEvent<HTMLDivElement>,
  ) => {
    if (event.key === "Escape") {
      // Stop the native bubble too. The deal drawer
      // listens for `Escape` at the window level to
      // close itself; without this both handlers fire
      // and the drawer slides away along with the
      // picker.
      event.preventDefault()
      event.stopPropagation()
      event.nativeEvent.stopImmediatePropagation()
      onClose()
      return
    }
    // Numeric shortcuts 1..N — bare digits, no modifier.
    // The popover is focused on mount so this works
    // without the user clicking inside first.
    const digit = Number(event.key)
    if (
      Number.isInteger(digit) &&
      digit >= 1 &&
      digit <= stages.length
    ) {
      const target = stages[digit - 1]
      if (target) {
        event.preventDefault()
        moveToStage(target)
      }
    }
  }

  // Right-align under the trigger, clamped into the
  // viewport. 6px gap matches `mt-1.5`.
  const top = anchorRect.bottom + 6
  const left = Math.max(
    12,
    Math.min(
      anchorRect.right - POPOVER_WIDTH,
      window.innerWidth - POPOVER_WIDTH - 12,
    ),
  )

  return (
    <div
      ref={setRefs}
      tabIndex={-1}
      role="listbox"
      aria-label={t`Move to stage`}
      onKeyDown={handleKeyDown}
      style={{ top, left, width: POPOVER_WIDTH }}
      className="fixed z-50 flex flex-col gap-[3px] rounded-xl py-1.5 px-1.5 bg-white border border-[#E5DFD3] shadow-[0_12px_28px_rgba(30,27,23,0.12),0_2px_6px_rgba(30,27,23,0.06)] outline-none"
    >
      <Header />
      {stages.map((stage, index) => (
        <StageOption
          key={stage.id}
          stage={stage}
          shortcut={index + 1}
          isCurrent={stage.id === deal.stage.id}
          disabled={submitting}
          tintBackground={
            tints.get(stage.id)?.background ?? "transparent"
          }
          onSelect={() => moveToStage(stage)}
        />
      ))}
    </div>
  )
}

function Header() {
  return (
    <div className="flex items-center justify-between pt-1.5 pb-2 px-2.5">
      <span className="tracking-[0.08em] uppercase font-semibold text-[#9A938A] text-[10px] leading-3">
        <Trans>Move to stage</Trans>
      </span>
      <span className="text-[#9A938A] text-[10px] leading-3">
        esc
      </span>
    </div>
  )
}

function StageOption({
  stage,
  shortcut,
  isCurrent,
  disabled,
  tintBackground,
  onSelect,
}: {
  stage: LinkedDealPipelineStage
  shortcut: number
  isCurrent: boolean
  disabled: boolean
  tintBackground: string
  onSelect: () => void
}) {
  // Selected row overlays the canonical dark fill on top
  // of the journey tint; unselected rows wear the tint
  // itself. Matches MQV-0.
  const background = isCurrent ? STAGE_SELECTED_BG : tintBackground
  const labelColor = isCurrent ? STAGE_SELECTED_FG : "#1E1B17"
  const shortcutColor = isCurrent ? STAGE_SELECTED_FG : "#7A746B"
  const labelWeight = isCurrent ? "font-semibold" : "font-normal"

  return (
    <button
      type="button"
      role="option"
      aria-selected={isCurrent}
      disabled={disabled}
      onClick={onSelect}
      data-testid={`stage-option-${stage.id}`}
      style={{ backgroundColor: background }}
      className="flex items-center rounded-lg py-2 px-2.5 gap-2.5 cursor-pointer disabled:cursor-not-allowed disabled:opacity-60"
    >
      <span
        aria-hidden="true"
        className="rounded-full shrink-0 size-2"
        style={{ backgroundColor: STAGE_DOT_HEX[stage.stageType] }}
      />
      <span
        className={`grow basis-0 text-left text-[13px] leading-4 ${labelWeight}`}
        style={{ color: labelColor }}
      >
        {stage.name}
      </span>
      {isCurrent ? (
        <CheckIcon />
      ) : (
        <span
          className="font-normal text-[11px] leading-[14px]"
          style={{ color: shortcutColor }}
        >
          {shortcut}
        </span>
      )}
    </button>
  )
}

function ChevronDown() {
  return (
    <svg
      width="10"
      height="10"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
      style={{ flexShrink: 0 }}
    >
      <path
        d="M4 6L8 10L12 6"
        stroke="#7A746B"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function CheckIcon() {
  // On the dark-fill selected row the check reads in the
  // same cream tone as the label — matches MQV-0.
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
      style={{ flexShrink: 0 }}
    >
      <path
        d="M3 8L7 12L13 5"
        stroke={STAGE_SELECTED_FG}
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}
